import React, { useState, useEffect } from 'react';
import { 
  BellIcon,
  ChatAlt2Icon,
  DocumentTextIcon,
  ExclamationIcon,
  CheckCircleIcon,
  ClockIcon,
  StarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  TrendingUpIcon,
  FilterIcon,
  SearchIcon,
  XIcon,
  EyeIcon,
  ArchiveIcon,
  LocationMarkerIcon
} from '@heroicons/react/outline';
import { useAuth } from '../context/AuthContext';

const SupplierNotificationCenter = ({ isOpen, onClose }) => {
  const { user, userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // Mock notifications - replace with real data from Firebase
  const mockNotifications = [
    {
      id: 1,
      type: 'new_rfq',
      title: 'New RFQ: Office Cleaning Services',
      message: 'A new request for office cleaning services has been posted in your area.',
      priority: 'high',
      timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
      read: false,
      actionUrl: '/rfq/123',
      category: 'Cleaning',
      budget: 150000,
      location: 'Lagos'
    },
    {
      id: 2,
      type: 'quote_response',
      title: 'Quote Response Received',
      message: 'TechCorp Nigeria has responded to your quote for IT equipment.',
      priority: 'medium',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      read: false,
      actionUrl: '/communication/456',
      category: 'IT Equipment',
      budget: 200000,
      location: 'Abuja'
    },
    {
      id: 3,
      type: 'negotiation',
      title: 'Negotiation Update',
      message: 'Restaurant Chain NG wants to discuss bulk pricing for rice supply.',
      priority: 'high',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
      read: true,
      actionUrl: '/communication/789',
      category: 'Food Supply',
      budget: 500000,
      location: 'Port Harcourt'
    },
    {
      id: 4,
      type: 'reminder',
      title: 'Follow-up Reminder',
      message: 'Don\'t forget to follow up on your quote for PrintMaster Ltd.',
      priority: 'low',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
      read: true,
      actionUrl: '/communication/101',
      category: 'Printing',
      budget: 75000,
      location: 'Kano'
    },
    {
      id: 5,
      type: 'success',
      title: 'Quote Accepted!',
      message: 'Congratulations! Your quote for StartupHub Lagos has been accepted.',
      priority: 'high',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      read: true,
      actionUrl: '/communication/202',
      category: 'Office Equipment',
      budget: 200000,
      location: 'Lagos'
    }
  ];

  useEffect(() => {
    // Simulate loading data
    setTimeout(() => {
      setNotifications(mockNotifications);
      setLoading(false);
    }, 1000);
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_rfq': return <DocumentTextIcon className="h-5 w-5 text-blue-600" />;
      case 'quote_response': return <ChatAlt2Icon className="h-5 w-5 text-green-600" />;
      case 'negotiation': return <UserGroupIcon className="h-5 w-5 text-purple-600" />;
      case 'reminder': return <ClockIcon className="h-5 w-5 text-orange-600" />;
      case 'success': return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      default: return <BellIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return <ExclamationIcon className="h-4 w-4 text-red-500" />;
      case 'medium': return <TrendingUpIcon className="h-4 w-4 text-yellow-500" />;
      case 'low': return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      default: return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || notification.type === filter;
    return matchesSearch && matchesFilter;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  const archiveNotification = (notificationId) => {
    setNotifications(prev => 
      prev.filter(notification => notification.id !== notificationId)
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <BellIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Notification Center</h2>
                <p className="text-orange-100">{unreadCount} unread notifications</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={markAllAsRead}
                className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors text-sm"
              >
                Mark All Read
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="new_rfq">New RFQs</option>
                <option value="quote_response">Quote Responses</option>
                <option value="negotiation">Negotiations</option>
                <option value="reminder">Reminders</option>
                <option value="success">Success</option>
              </select>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <FilterIcon className="h-4 w-4" />
              <span>{filteredNotifications.length} notifications</span>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <BellIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-6 hover:bg-gray-50 transition-colors ${
                    !notification.read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {notification.title}
                            </h3>
                            {!notification.read && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                New
                              </span>
                            )}
                            {getPriorityIcon(notification.priority)}
                          </div>
                          
                          <p className="text-gray-600 mb-3">
                            {notification.message}
                          </p>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                            <span className="flex items-center">
                              <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                              {formatCurrency(notification.budget)}
                            </span>
                            <span className="flex items-center">
                              <LocationMarkerIcon className="h-4 w-4 mr-1" />
                              {notification.location}
                            </span>
                            <span className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              {notification.category}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="flex items-center text-sm text-orange-600 hover:text-orange-700"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              {notification.read ? 'Mark as unread' : 'Mark as read'}
                            </button>
                            <button
                              onClick={() => archiveNotification(notification.id)}
                              className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                            >
                              <ArchiveIcon className="h-4 w-4 mr-1" />
                              Archive
                            </button>
                            <a
                              href={notification.actionUrl}
                              className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                            >
                              <ChatAlt2Icon className="h-4 w-4 mr-1" />
                              View Details
                            </a>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end space-y-2">
                          <span className="text-sm text-gray-500">
                            {formatTime(notification.timestamp)}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(notification.priority)}`}>
                            {notification.priority.charAt(0).toUpperCase() + notification.priority.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {unreadCount} unread • {notifications.length} total notifications
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={markAllAsRead}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Mark All Read
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierNotificationCenter; 